import { useMutation } from "@tanstack/react-query";
import React, { useEffect } from "react";

import { BellAlertIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  EnvelopeIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/solid";
import { PageProps, Script, navigate } from "gatsby";
import { useForm } from "react-hook-form";
import { SERPPageContext } from "../../../gatsby-node";
import type { SERPConfig } from "../../../plugins/gatsby-source-serp/types";
import { ModalState } from "../../../plugins/gatsby-source-serp/types";
import JornayaScript from "../../components/JornayaScript";
import Loader from "../../components/Loader";
import InputWithLabel from "../../components/forms/InputWithLabel";
import { trackCustomEvent, trackCustomException } from "../../utils/analytics";
import coReg, { RegisterInputProps } from "../../utils/coReg";
import getNavigatePath from "../../utils/getNavigatePath";
import { CredentialResponse, onSuccess } from "../path/googleAuth";
import type { FormValues } from "./Serp";

export type DataCollectionModal = {
  jbeModal: SERPConfig["jbeModal"];
  showJobModalOnJbeCloseAndSubmit: SERPConfig["showJobModalOnJbeCloseAndSubmit"];
  toggleModalState: (T: ModalState | null) => void;
  location: PageProps["location"];
  serpPathWithoutModal: SERPConfig["serpPathWithoutModal"];
  serpPathWithJobModal: SERPConfig["serpPathWithJobModal"];
  job: any;
  onSubmit: (T: FormValues) => void;
  tx: any;
  values: FormValues;
  addExitIntentHash: () => void;
  googleOneTap: boolean;
  googleSignIn: boolean;
  compareWithConfig: PageProps<
    Queries.SERPPageQuery,
    SERPPageContext
  >[`data`][`serp`][`compareWithConfig`];
};

function getURLFromValues(data: FormValues, nextPath: string) {
  data.position = data.position.trim(); // " " => ""
  const path = getNavigatePath(data, nextPath);
  return path;
}

export default function ModalDCForm({
  tx,
  toggleModalState,
  jbeModal,
  values,
  compareWithConfig,
  serpPathWithoutModal,
  serpPathWithJobModal,
  location,
  showJobModalOnJbeCloseAndSubmit,
  googleOneTap,
  googleSignIn,
  addExitIntentHash,
}: DataCollectionModal) {
  useEffect(() => {
    trackCustomEvent({
      category: "SERP",
      action: `Modal load: ${jbeModal}`,
      label: location.pathname,
    });
  }, [jbeModal, location.pathname]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    formState: { errors, touchedFields },
  } = useForm<RegisterInputProps>({
    defaultValues: {
      ...values,
      compare: !!compareWithConfig, // only set this to true if compareWithConfig is defined
    },
    mode: "onBlur",
  });

  function triggerClose() {
    const dcFormValues = getValues();
    if (dcFormValues.compare) {
      trackCustomEvent({
        category: "SERP",
        action: "Modal close",
        label: `${location.pathname} with compare`,
      });
      window.open(getURLFromValues(values, serpPathWithoutModal));
      const href = getURLFromValues(values, compareWithConfig.url);
      if (window.gtag) {
        window.gtag("event", "Modal close", {
          event_category: "outbound",
          event_label: href,
          transport_type: "beacon",
          event_callback: function event_callback() {
            document.location = href;
          },
          event_timeout: 2000, // fallback to trigger the redirect in event_callback even if the tags stall
        });
      } else {
        document.location = href;
      }
    } else {
      toggleModalState(showJobModalOnJbeCloseAndSubmit ? ModalState.job : null);
    }
  }

  const { mutate, isLoading } = useMutation(coReg, {
    onSuccess: (data) => {
      if (data.compare) {
        // same tab:  partner
        trackCustomEvent({
          category: "SERP",
          action: `Data opt-in`,
          label: `${location.pathname} with compare`,
        });
        // new tab: BJO
        const href = getURLFromValues(data, compareWithConfig.url);
        if (window.gtag) {
          window.gtag("event", "Compare", {
            event_category: "outbound",
            event_label: href,
            transport_type: "beacon",
            event_callback: function event_callback() {
              document.location = href;
            },
            event_timeout: 2000, // fallback to trigger the redirect in event_callback even if the tags stall
          });
        } else {
          document.location = href;
        }
      } else {
        trackCustomEvent({
          category: "SERP",
          action: `Data opt-in`,
          label: location.pathname,
        });

        if (showJobModalOnJbeCloseAndSubmit) {
          // If the job query vcalues have changed, there's a chance the first job query's
          // job modal will render after the DC modal closes, but before the _new_ job query is loaded.
          // This is a rendering bug. So instead, we can navigate directly to serp/3 to see the correct
          // job modal.
          const path = getURLFromValues(data, serpPathWithJobModal);
          navigate(path);
        } else {
          // otherwise close this modal
          toggleModalState(null);
        }
      }
    },
    onError: (error: Error) => {
      trackCustomEvent({
        category: "SERP",
        action: `Data opt-in error`,
        label: `${window.location.pathname} [${error.message}]`,
      });
      trackCustomException(error.message, false);

      try {
        const errors = JSON.parse(error.message);
        if (errors.includes("INVALID_EMAIL")) {
          setError("email", { type: "validate" });
        }
        if (errors.includes("INVALID_PHONE")) {
          setError("phone", { type: "validate" });
        }
      } catch (err) {
        console.error(err);
      }
    },
  });

  function onModalDCFormSubmit(data) {
    addExitIntentHash?.();
    // immediately continues BJO in new tab.
    // meanwhile, continues to validate and load partner in the same tab
    // see https://qrn-channel.slack.com/archives/C040TRNNL1J/p1666366699839019
    if (data.compare) {
      const href = getURLFromValues(data, serpPathWithoutModal);
      window.open(href);
    }
    mutate({ ...data, source: "serp" });
  }

  const onSubmit = handleSubmit(onModalDCFormSubmit);

  return (
    <div className="p-5">
      {googleOneTap && (
        <Script
          src="https://accounts.google.com/gsi/client"
          onLoad={() => {
            window.google.accounts.id.initialize({
              client_id:
                "243738620951-on9rl73860jgm4jaovk8tensc7j8dc7f.apps.googleusercontent.com",
              callback: (response: CredentialResponse) =>
                onSuccess(
                  response,
                  "SERP",
                  location.pathname,
                  setValue,
                  onSubmit
                ),
              cancel_on_tap_outside: false,
            });
            window.google.accounts.id.prompt(); // also display the One Tap dialog
          }}
        />
      )}
      {googleSignIn && (
        <Script
          src="https://accounts.google.com/gsi/client"
          onLoad={() => {
            window.google.accounts.id.initialize({
              client_id:
                "243738620951-on9rl73860jgm4jaovk8tensc7j8dc7f.apps.googleusercontent.com",
              callback: (response: CredentialResponse) =>
                onSuccess(
                  response,
                  "SERP",
                  location.pathname,
                  setValue,
                  onSubmit
                ),
            });
            window.google.accounts.id.renderButton(
              document.getElementById("google-signin"),
              {
                theme: "filled_blue",
                text: "continue_with",
                logo_alignment: "left",
                size: "large",
                type: "standard",
              }
            );
          }}
        />
      )}
      <div className="flex items-start justify-between">
        <h1 className="mb-4 flex items-baseline text-2xl font-extrabold capitalize tracking-tight text-gray-900 sm:text-2xl md:text-3xl">
          <BellAlertIcon className="mr-4 h-5 w-5 grow text-[#D40063] hover:animate-shake" />
          <span className="shrink">
            {tx!.collectionTitle.replace("{{position}}", values.position || "")}
          </span>
        </h1>
        <button
          data-cy="modal-close"
          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={triggerClose}
        >
          <span className="sr-only">Close</span>
          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <p className="mb-4 text-lg leading-6 text-gray-500">
        {tx!.collectionSubtitle.replace("{{position}}", values.position)}
      </p>
      <form
        method="get"
        className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
        onSubmit={onSubmit}
      >
        <InputWithLabel
          name="position"
          type="text"
          touchedFields={touchedFields}
          errors={errors}
          register={register}
          tx={tx}
          placeholder={tx!.positionLabel}
          Icon={MagnifyingGlassIcon}
          required
          autoFocus
          disabled={isLoading}
        />
        <InputWithLabel
          name="location"
          type="text"
          touchedFields={touchedFields}
          errors={errors}
          register={register}
          tx={tx}
          placeholder={tx!.locationLabel}
          Icon={MapPinIcon}
          required
          disabled={isLoading}
        />
        {googleSignIn && (
          <div className="sm:col-span-2">
            <div id="google-signin" />
            <div className="relative col-span-2">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-sm text-gray-500">OR</span>
              </div>
            </div>
          </div>
        )}
        <InputWithLabel
          name="email"
          type="email"
          touchedFields={touchedFields}
          errors={errors}
          register={register}
          tx={tx}
          placeholder={tx!.collectionEmailPlaceholder}
          invalidText={tx!.collectionEmailInvalid}
          Icon={EnvelopeIcon}
          required
          disabled={isLoading}
        />
        {jbeModal === ModalState["email,sms"] && (
          <InputWithLabel
            Icon={PhoneIcon}
            name={"phone"}
            touchedFields={touchedFields}
            errors={errors}
            invalidText={tx!.collectionPhoneInvalid}
            placeholder={tx!.collectionPhonePlaceholder}
            register={register}
            required
            tx={tx}
            type="tel"
            disabled={isLoading}
          />
        )}
        <JornayaScript />
        <button
          type="submit"
          className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 disabled:bg-opacity-40 sm:col-span-2"
          disabled={isLoading}
        >
          {isLoading && <Loader className="mr-2" />}
          {tx!.collectionActivate}
        </button>
        {compareWithConfig && (
          <fieldset className="relative flex items-start justify-center sm:col-span-2">
            <div className="flex h-5 items-center">
              <input
                id="compare"
                aria-describedby={tx!.collectionCompareWith.replace(
                  "{{comparePartner}}",
                  compareWithConfig.partner
                )}
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                {...register("compare")}
              />
            </div>
            <label
              htmlFor="compare"
              className="ml-3 text-sm font-medium text-gray-700"
            >
              {tx!.collectionCompareWith.replace(
                "{{comparePartner}}",
                compareWithConfig.partner
              )}
            </label>
          </fieldset>
        )}
        <p
          className="text-xs sm:col-span-2"
          dangerouslySetInnerHTML={{
            __html:
              jbeModal === ModalState.email
                ? tx!.collectionDisclaimerEmail
                : tx!.collectionDisclaimerEmailSMS,
          }}
        />
      </form>
    </div>
  );
}
